import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft } from 'lucide-react'
import { formatInteger } from 'utils/formatting'
import { SAMPLES_TABLE_KEY } from 'constants/tableQueryKeys'
import { Tabs } from 'components/Tabs'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { MobileSampleCard } from 'components/Samples/MobileSampleCard'
import { FbLink } from 'components/FbUI/FbLink'
import { useGetSamplesInfinite, useGetSamplesCounts } from 'services/samplesApi'
import {
  SampleRequest,
  SampleRequestsListResponse,
  SampleRequestFilters,
} from 'models/samples'
import { MobileColors } from 'styles/mobileTokens'
import { PageContainer } from 'components/MobileSearch/MobileSearch.styles'
import { BackButton } from 'components/Mobile/Mobile.styles'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Date Created" type="number" />,
    value: 'created',
  },
  {
    label: <SortingOptionLabel label="Date Created" type="number" desc />,
    value: '-created',
  },
  {
    label: <SortingOptionLabel label="Need By" type="number" />,
    value: 'date_needed',
  },
  {
    label: <SortingOptionLabel label="Need By" type="number" desc />,
    value: '-date_needed',
  },
  {
    label: <SortingOptionLabel label="Request #" />,
    value: 'request_number',
  },
  {
    label: <SortingOptionLabel label="Request #" desc />,
    value: '-request_number',
  },
]

const ShowInactiveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0px 8px;
  font-size: 14px;
  color: #374151;
  justify-content: end;
`

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 10px 0 0 0;
  padding: 0 16px 8px;
`

const CompanyLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px 8px;
  font-size: 14px;
  color: ${MobileColors.textSecondary};
`

export function MobileSamplesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isLimited } = useAuth()
  const { enableUserTypes } = useFeatureFlagService()
  const showNeedsReviewTab = enableUserTypes && !isLimited
  const initialTab = searchParams.get('tab')
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    if (initialTab === 'needs_review' && showNeedsReviewTab)
      return 'needs_review'
    if (initialTab === 'all_samples' && !isLimited) return 'all_samples'
    return 'my_samples'
  })
  const [showInactive, setShowInactive] = useState<boolean>(false)

  const handleTabChange = (key: string) => {
    setSelectedTab(key)
    const next = new URLSearchParams(searchParams)
    if (key === 'my_samples') next.delete('tab')
    else next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  // Query params for filtering by company
  const contactCompanyId = searchParams.get('contact_company_id')
  const companyName = searchParams.get('company_name')

  const isCompanyFiltered = !!contactCompanyId

  const IDENTIFIER = 'MOBILE_SAMPLES_TABLE'

  const { data: samplesCounts } = useGetSamplesCounts(
    showInactive ? {} : { status: 'submitted' },
    { needsMyReview: showNeedsReviewTab }
  )
  const sortingStore = dataTableSortingStoreRepo.getStore(SAMPLES_TABLE_KEY)()

  const samplesFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = samplesFilterStore()
  const { page, setPage } = filterStore

  const { sorting } = sortingStore

  const filtersAsParams = {
    ...useFilterParams(samplesFilterStore),
  }

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }
    return params
  }, [filtersAsParams])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['ordering'] = sorting[0].desc
        ? '-' + sorting[0]?.id
        : sorting[0]?.id
    }

    return params
  }, [sorting])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [selectedTab])

  const filterAndSortParams = useMemo<SampleRequestFilters>(
    () => ({
      ...filterParams,
      ...sortParams,
      ...(selectedTab === 'my_samples' ? { my_requests_only: true } : {}),
      ...(selectedTab === 'needs_review'
        ? { status: 'pending_review' as const, needs_my_review: true }
        : showInactive
          ? {}
          : { status: 'submitted' as const }),
      ...(contactCompanyId
        ? { contact_company_id: parseInt(contactCompanyId, 10) }
        : {}),
    }),
    [filterParams, sortParams, selectedTab, showInactive, contactCompanyId]
  )

  const query = useGetSamplesInfinite({
    page,
    page_size: 10,
    ...filterAndSortParams,
  })

  const handlePreviewSample = (sampleId: number) => {
    navigate(`/samples/${sampleId}`)
  }

  return (
    <PageContainer>
      {/* Show back button and header when filtering by company */}
      {isCompanyFiltered && (
        <>
          <BackButton onClick={() => navigate(-1)}>
            <ChevronLeft size={20} />
            <span>Back</span>
          </BackButton>

          <PageTitle>Samples</PageTitle>

          {companyName && (
            <CompanyLinks>
              <FbLink to={`/companies/${contactCompanyId}`}>
                {companyName}
              </FbLink>
            </CompanyLinks>
          )}
        </>
      )}

      {/* Show tabs only when NOT filtering by company */}
      {!isCompanyFiltered && (
        <div className="w-full mt-3 ml-2">
          <Tabs
            tabs={[
              {
                key: 'my_samples',
                title: `My Samples (${formatInteger(samplesCounts?.my_samples_count)})`,
              },
              ...(!isLimited
                ? [
                    {
                      key: 'all_samples',
                      title: `All Samples (${formatInteger(samplesCounts?.all_samples_count)})`,
                    },
                  ]
                : []),
              ...(showNeedsReviewTab
                ? [
                    {
                      key: 'needs_review',
                      title: `Needs Review (${formatInteger(samplesCounts?.needs_my_review_count)})`,
                    },
                  ]
                : []),
            ]}
            selectedTab={selectedTab}
            setSelectedTab={handleTabChange}
          />
        </div>
      )}

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={SAMPLES_TABLE_KEY}
        defaultSorting={[{ id: 'created', desc: true }]}
      />

      <ShowInactiveContainer>
        <input
          type="checkbox"
          id="showInactive"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        <label htmlFor="showInactive">Show Inactive</label>
      </ShowInactiveContainer>

      <FilterChips
        store={samplesFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <MobileInfiniteScrollView<SampleRequestsListResponse, SampleRequest>
        extractItems={(page) => page.results}
        infiniteQuery={query}
      >
        {(samples) =>
          samples?.map((sample: SampleRequest) => (
            <MobileSampleCard
              key={sample.id}
              sample={sample}
              onPreview={handlePreviewSample}
            />
          ))
        }
      </MobileInfiniteScrollView>
    </PageContainer>
  )
}
